import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TableRow,
  TableHead,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TextField,
} from "@mui/material";
import Header from "components/Header";
import { useTheme } from "@emotion/react";
import FlexBetween from "components/FlexBetween";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRoleAccess, updateRoleAccess } from "service/permission/RoleAccess";
import { getRole } from "service/permission/Role";
import ShowFullScreenBackdrop from "components/ShowFullScreenBackdrop";

const RoleAccess = () => {
  const theme = useTheme();
  const [permissions, setPermissions] = useState([]);
  const [permissionId, setPermissionId] = useState("");
  const [selectedRole, setSelectedRole] = useState(1);
  const [roleData, setRoleData] = useState([]);
  const [modifiedPermissions, setModifiedPermissions] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchRoleAccessList = async () => {
    try {
      const response = await getRoleAccess(selectedRole);
      setPermissions(response.data.data.permissions);
      setPermissionId(response.data.data._id);
    } catch (error) {
      console.error(
        "There was a problem fetching the role access list:",
        error
      );
    }
  };

  const fetchRoleList = async () => {
    try {
      const response = await getRole();
      setRoleData(response.data.data);
    } catch (error) {
      console.error("There was a problem fetching the role list:", error);
    }
  };

  useEffect(() => {
    fetchRoleList();
  }, []);

  useEffect(() => {
    fetchRoleAccessList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  const handleUpdateButtonClick = async () => {
    try {
      setOpen(true);

      const updatedData = {
        roleId: selectedRole,
        permissions: modifiedPermissions.map((permissionIndex) => ({
          title: permissions[permissionIndex].title,
          access: permissions[permissionIndex].access,
        })),
      };
      await updateRoleAccess(updatedData, permissionId);
      toast.success("Role access updated successfully");
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error updating role access");
      }
    } finally {
      setOpen(false);
    }
  };

  const handlePermissionChange = (index, value) => {
    const updatedPermissions = [...permissions];
    updatedPermissions[index].access = value;
    setPermissions(updatedPermissions);
    if (!modifiedPermissions.includes(index)) {
      setModifiedPermissions([...modifiedPermissions, index]);
    }
  };

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <Header title="ROLE ACCESS" subTitle="Role Access" />
        <Box mt="40px" height="80vh">
          <FlexBetween>
            <TextField
              label="Select Role"
              select
              size="small"
              color="secondary"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              sx={{
                mb: "1rem",
                width: "10rem",
                ml: "auto",
              }}
            >
              {roleData.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.role}
                </MenuItem>
              ))}
            </TextField>
          </FlexBetween>

          <Box
            sx={{
              ".css-1o0jeq9-MuiTableCell-root, .css-1ska7s8-MuiTableCell-root, .css-1fj07e2-MuiTableCell-root, .css-17v7tf6-MuiTableCell-root":
                {
                  backgroundColor: theme.palette.background.alt,
                  color: theme.palette.secondary[100],
                },
              ".css-13fuhd2-MuiPaper-root-MuiTableContainer-root, .css-1l2qa2v-MuiPaper-root-MuiTableContainer-root":
                {
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.secondary[100],
                },
              "& .css-1fj07e2-MuiTableCell-root": {
                lineHeight: "2.8rem",
              },
              "& .css-u6s4ls-MuiTableRow-root": {
                height: "3.5rem",
              },
            }}
          >
            <TableContainer
              sx={{
                overflowX: "initial",
                overflowY: "auto",
                maxHeight: "350px",
              }}
              component={Paper}
            >
              <Table stickyHeader aria-label="a dense table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S.NO</TableCell>
                    <TableCell>LIST OF MODULES</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {permissions.map((permission, index) => (
                    <TableRow key={permission.title}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{permission.title}</TableCell>
                      <TableCell align="center">
                        <TextField
                          select
                          size="small"
                          value={permission.access}
                          color="secondary"
                          onChange={(e) =>
                            handlePermissionChange(index, e.target.value)
                          }
                          sx={{
                            width: "10rem",
                          }}
                        >
                          <MenuItem value="viewOnly">View Only</MenuItem>
                          <MenuItem value="hide">Hide</MenuItem>
                          <MenuItem value="write">Write Permission</MenuItem>
                        </TextField>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <FlexBetween>
            <FlexBetween></FlexBetween>
            <Button
              variant="contained"
              color="secondary"
              disabled={modifiedPermissions.length === 0 || open}
              onClick={handleUpdateButtonClick}
              sx={{ mt: 2, width: "10rem" }}
            >
              Update
            </Button>
          </FlexBetween>
        </Box>
      </Box>
      {open && <ShowFullScreenBackdrop open={open} />}
    </>
  );
};

export default RoleAccess;
